/**
 * Sessions sidebar — paginated vertical list of persisted sessions
 * for the active project, anchored to the left of the chat.
 *
 * Layout (top → bottom):
 *
 *   - **Header**: the project basename and collapsed home-relative
 *     path as a passive orientation surface.
 *   - **New session button**: a dashed affordance at the top of
 *     the session list.
 *   - **Grouped session list**: rows are grouped by recency
 *     ("Today" / "Yesterday" / "This week" / "Earlier") under
 *     subtle uppercase labels. Each row has a locked min-height so
 *     the layout never jumps when toggling rename, is clickable
 *     (resume), inline-renamable, and deletable.
 *
 * Status indicators per row:
 *
 *   - **In-progress spinner** — subtle loader when the foreground
 *     session's live reducer would also show the footer-level
 *     "Working..." indicator. Background rows still fall back to
 *     the workspace server's ``is_active`` flag because they do
 *     not have a mounted reducer in this browser.
 *   - **Unread dot** — solid blue when ``max(disk last_event_at,
 *     foreground lastEventAt) > seen[sessionId]``.
 *
 * Polling: a 15s sweep keeps the disk-polled list (renames,
 * deletes, sessions touched by other tabs / CLIs) in sync.
 * Server-resident sessions tick their busy state into the same
 * endpoint, so polling also catches "background session became
 * active" within 15 seconds.
 */

import { Box, MessagesSquare, Pencil, Plus, Trash2, X } from 'lucide-react';
import * as React from 'react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ErrorState } from '@/components/ui/error-state';
import { Spinner } from '@/components/ui/spinner';
import { SimpleTooltip } from '@/components/ui/tooltip';
import type { AgentSessionEntry } from '@/lib/agent/api';
import { deleteSession, fetchSessionsPage, renameSession } from '@/lib/agent/api';
import { shouldShowWorkingIndicator } from '@/lib/agent/reducer';
import { clearSessionPrefs, workspaceScopedKey } from '@/lib/agent/session-persistence';
import { diskActivityAt, displayActivityAt } from '@/lib/agent/sessionRecency';
import { nextSessionIdAfterDelete } from '@/lib/agent/sessionSelection';
import { cn } from '@/lib/utils/cn';
import { formatRelativeFromEpochMs } from '@/lib/utils/date';
import { useAgentPanel } from './AgentPanelProvider';

/**
 * Sweep cadence for refreshing the on-disk session list. The
 * workspace server's ``/project/sessions`` endpoint reports
 * ``is_active`` and ``last_event_at`` per session — both
 * server-resident, so polling at 15s is enough to catch
 * background-session activity (and renames / deletes / new
 * sessions touched by other tabs or the CLI). Foreground state
 * comes from the live runtime in-process and updates instantly,
 * so it doesn't depend on this cadence.
 */
const POLL_INTERVAL_MS = 15_000;
const SESSION_PAGE_SIZE = 50;
const SESSION_REFRESH_LIMIT = 100;

/**
 * ``localStorage`` namespace for the per-session "last seen"
 * timestamps that drive the unread dot. One entry per
 * ``(workspaceId, cwd)`` holding ``{ [sessionId]: lastEventEpochMs }``.
 * ``workspaceId`` is part of the key (mirroring ``session-persistence``)
 * because two workspaces can share a ``cwd``, and their session ids
 * could otherwise pollute one another's unread baselines.
 */
const SEEN_KEY_PREFIX = 'antioch:session-seen:';

/** Compose the workspace-scoped seen-map key for a ``cwd``. */
function seenKey(workspaceId: string | null, cwd: string): string {
  return `${SEEN_KEY_PREFIX}${workspaceScopedKey(workspaceId, cwd)}`;
}

function readSeen(workspaceId: string | null, cwd: string): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(seenKey(workspaceId, cwd));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return isSeenMap(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function isSeenMap(value: unknown): value is Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value).every((timestamp) => typeof timestamp === 'number' && Number.isFinite(timestamp));
}

function sessionsEqual(a: AgentSessionEntry[] | null, b: AgentSessionEntry[]): boolean {
  if (!a || a.length !== b.length) return false;
  for (let index = 0; index < a.length; index++) {
    const left = a[index];
    const right = b[index];
    if (
      left.session_id !== right.session_id ||
      left.first_prompt !== right.first_prompt ||
      left.summary !== right.summary ||
      left.last_modified !== right.last_modified ||
      left.last_event_at !== right.last_event_at ||
      left.created_at !== right.created_at ||
      Boolean(left.is_active) !== Boolean(right.is_active)
    ) {
      return false;
    }
  }
  return true;
}

function mergeSessionPage(existing: AgentSessionEntry[], page: AgentSessionEntry[]): AgentSessionEntry[] {
  const byId = new Map(existing.map((session) => [session.session_id, session]));
  const out = existing.map((session) => byId.get(session.session_id) ?? session);
  for (const session of page) {
    if (byId.has(session.session_id)) {
      const index = out.findIndex((item) => item.session_id === session.session_id);
      if (index >= 0) out[index] = session;
      byId.set(session.session_id, session);
      continue;
    }
    byId.set(session.session_id, session);
    out.push(session);
  }
  return out;
}

function mergeRefreshedPrefix(existing: AgentSessionEntry[] | null, page: AgentSessionEntry[]): AgentSessionEntry[] {
  if (!existing || existing.length <= page.length) return page;
  const pageIds = new Set(page.map((session) => session.session_id));
  return [...page, ...existing.filter((session) => !pageIds.has(session.session_id))];
}

function loadedRefreshLimit(nextOffset: number, currentLength: number | null | undefined): number {
  return Math.min(Math.max(nextOffset, currentLength ?? 0, SESSION_PAGE_SIZE), SESSION_REFRESH_LIMIT);
}

function writeSeen(workspaceId: string | null, cwd: string, seen: Record<string, number>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(seenKey(workspaceId, cwd), JSON.stringify(seen));
  } catch {
    // Quota / disabled storage — fall through silently.
  }
}

/**
 * Per-row status fused from the live foreground runtime + the
 * disk-polled session entry. Computed once per render in
 * :class:`SessionSidebar` so :class:`SessionRow` stays a pure
 * render of the result.
 */
interface RowStatus {
  /** ``true`` when the agent is actively producing output for this session. */
  inProgress: boolean;
  /** ``true`` when there's new activity since the user last opened the row. */
  unread: boolean;
}

export interface LiveForegroundSession {
  sessionId: string;
  inProgress: boolean;
  lastEventAt: number;
}

export interface SessionSidebarProps {
  /** Absolute project cwd this sidebar is bound to. */
  cwd: string;
  /** Currently-active session id, if any — used to highlight a row. */
  currentSessionId: string | null;
  /** Resume / start a session in the same project. */
  onChooseSession: (sessionId: string | null) => void;
  className?: string;
}

export function SessionSidebar({ cwd, currentSessionId, onChooseSession, className }: SessionSidebarProps) {
  // The panel context is the single source of truth for which workspace
  // this panel (and therefore this sidebar) is bound to. Re-deriving it
  // from ``useWorkspace()`` would create a second source that can
  // disagree under story/test mocks.
  const { workspaceId } = useAgentPanel();
  const [sessions, setSessions] = React.useState<AgentSessionEntry[] | null>(null);
  // Sessions the user hard-deleted this mount, suppressed from the list for
  // good. A hard delete is terminal, but the disk-poll's prefix-merge can't
  // tell a vanished session from an older tail row and re-adds it, and the
  // live-foreground synth would resurrect a just-deleted *active* session until
  // the runtime switches away — so a local removal alone isn't enough.
  const [deletedIds, setDeletedIds] = React.useState<ReadonlySet<string>>(() => new Set<string>());
  const [error, setError] = React.useState<string | null>(null);
  const [hasMoreSessions, setHasMoreSessions] = React.useState(false);
  const [nextSessionOffset, setNextSessionOffset] = React.useState(0);
  const [loadingMoreSessions, setLoadingMoreSessions] = React.useState(false);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const page = await fetchSessionsPage(cwd, { limit: SESSION_PAGE_SIZE, offset: 0 });
      setSessions(page.sessions);
      setHasMoreSessions(Boolean(page.has_more));
      setNextSessionOffset(page.next_offset ?? page.sessions.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [cwd]);

  const refreshLoaded = React.useCallback(async () => {
    try {
      const loadedCount = loadedRefreshLimit(nextSessionOffset, sessions?.length);
      const page = await fetchSessionsPage(cwd, { limit: loadedCount, offset: 0 });
      setSessions((prev) => {
        const next = mergeRefreshedPrefix(prev, page.sessions);
        return sessionsEqual(prev, next) ? prev : next;
      });
      setHasMoreSessions(Boolean(page.has_more));
      setNextSessionOffset(Math.max(page.next_offset ?? page.sessions.length, sessions?.length ?? 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [cwd, nextSessionOffset, sessions?.length]);

  const loadMoreSessions = React.useCallback(async () => {
    if (loadingMoreSessions || !hasMoreSessions) return;
    setLoadingMoreSessions(true);
    try {
      const page = await fetchSessionsPage(cwd, { limit: SESSION_PAGE_SIZE, offset: nextSessionOffset });
      setSessions((prev) => mergeSessionPage(prev ?? [], page.sessions));
      setHasMoreSessions(Boolean(page.has_more));
      setNextSessionOffset(page.next_offset ?? nextSessionOffset + page.sessions.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMoreSessions(false);
    }
  }, [cwd, hasMoreSessions, loadingMoreSessions, nextSessionOffset]);

  // Drop the prior project's list when ``cwd`` changes so we don't
  // briefly show stale rows; refresh-driven loads keep the existing
  // list to avoid blank flashes.
  React.useEffect(() => {
    setSessions(null);
    setDeletedIds(new Set<string>());
    setHasMoreSessions(false);
    setNextSessionOffset(0);
    load();
  }, [load]);

  // Quiet poll keeps the disk-polled list in sync — covers
  // renames, deletes, brand-new sessions, and ``is_active``
  // transitions from the workspace server's busy roster (that's how
  // background-session in-progress dots stay current). Foreground
  // updates ride on the live runtime snapshot and don't wait for
  // this tick. Pause when the tab is hidden.
  React.useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.hidden) return;
      try {
        const loadedCount = loadedRefreshLimit(nextSessionOffset, sessions?.length);
        const next = await fetchSessionsPage(cwd, { limit: loadedCount, offset: 0 });
        if (!cancelled) {
          setSessions((prev) => {
            const merged = mergeRefreshedPrefix(prev, next.sessions);
            return sessionsEqual(prev, merged) ? prev : merged;
          });
          setHasMoreSessions(Boolean(next.has_more));
          setNextSessionOffset(Math.max(next.next_offset ?? next.sessions.length, sessions?.length ?? 0));
        }
      } catch {
        // Best-effort: explicit refresh button + next tick will retry.
      }
    };
    const handle = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [cwd, nextSessionOffset, sessions?.length]);

  // Live status for the FOREGROUND session only — keyed by session
  // id. Background sessions ride on the disk poll's ``is_active``
  // and ``last_event_at`` fields. The foreground gets in-process
  // freshness and deliberately uses the same predicate as the
  // footer-level "Working..." indicator so the sidebar spinner
  // never claims a completed row is still active after the footer
  // has switched to "Worked for …".
  const { state: liveState, cwd: panelCwd } = useAgentPanel();
  const liveForeground = React.useMemo(() => {
    if (panelCwd !== cwd) return null;
    const sid = liveState.sessionId ?? currentSessionId;
    if (!sid) return null;
    return {
      sessionId: sid,
      // Same predicate as the footer Working indicator -- keeps the
      // sidebar spinner from claiming a completed row is still active.
      inProgress: shouldShowWorkingIndicator(liveState),
      lastEventAt: liveState.lastEventAt,
    };
  }, [panelCwd, cwd, liveState, currentSessionId]);

  // Merge the foreground runtime's session id into the disk-polled
  // list immediately. A brand-new session attaches before the next
  // 15s sweep sees its JSONL on disk; without this synthesized
  // row, the active session would be invisible in the sidebar
  // for up to 15s.
  const visibleSessions = React.useMemo(
    () => mergeLiveForegroundSession(sessions ?? [], liveForeground).filter((s) => !deletedIds.has(s.session_id)),
    [sessions, liveForeground, deletedIds]
  );

  // Per-session "last seen" timestamps from localStorage. The unread
  // dot lights when the joint disk + pool timestamp is newer than
  // the stored baseline.
  const [seen, setSeen] = React.useState<Record<string, number>>(() => readSeen(workspaceId, cwd));
  React.useEffect(() => {
    setSeen(readSeen(workspaceId, cwd));
  }, [workspaceId, cwd]);

  const markSeen = React.useCallback(
    (sessionId: string, ts: number) => {
      if (!ts) return;
      setSeen((prev) => {
        if ((prev[sessionId] ?? 0) >= ts) return prev;
        const next = { ...prev, [sessionId]: ts };
        writeSeen(workspaceId, cwd, next);
        return next;
      });
    },
    [workspaceId, cwd]
  );

  // Bootstrap baseline for any session we see for the first time —
  // otherwise every old session lights its unread dot on the user's
  // first session-pool refresh, and other-tab activity shows as
  // unread the moment we observe it. Combined with the active-row
  // mark-seen below, this means a row only goes unread when
  // *new* activity lands after the row entered our local map.
  React.useEffect(() => {
    if (visibleSessions.length === 0) return;
    setSeen((prev) => {
      let mutated = false;
      const next = { ...prev };
      for (const s of visibleSessions) {
        if (s.session_id in next) continue;
        const ts = diskActivityAt(s);
        if (ts === 0) continue;
        next[s.session_id] = ts;
        mutated = true;
      }
      if (!mutated) return prev;
      writeSeen(workspaceId, cwd, next);
      return next;
    });
  }, [visibleSessions, workspaceId, cwd]);

  // Keep the active row continuously marked-seen as live events
  // arrive — otherwise the dot would ignite on the row the user is
  // actively reading every time the agent ticks. Joint timestamp:
  // foreground runtime's lastEventAt OR the disk activity timestamp.
  React.useEffect(() => {
    if (!currentSessionId) return;
    const live = liveForeground?.sessionId === currentSessionId ? liveForeground.lastEventAt : 0;
    const session = visibleSessions.find((s) => s.session_id === currentSessionId);
    const disk = session ? diskActivityAt(session) : 0;
    const ts = Math.max(live, disk);
    if (ts) markSeen(currentSessionId, ts);
  }, [currentSessionId, liveForeground, visibleSessions, markSeen]);

  // Per-row fused status. Computed once per render so SessionRow
  // stays a pure render of the result.
  const rowStatuses = React.useMemo(() => {
    const out: Record<string, RowStatus> = {};
    for (const s of visibleSessions) {
      const isForeground = liveForeground?.sessionId === s.session_id;
      const inProgress = isForeground ? liveForeground.inProgress : Boolean(s.is_active);
      const liveTs = isForeground ? liveForeground.lastEventAt : 0;
      const eventTs = Math.max(liveTs, diskActivityAt(s));
      const baseline = seen[s.session_id] ?? 0;
      const isActive = s.session_id === currentSessionId;
      out[s.session_id] = {
        inProgress,
        // Active row never reads as unread — the user is looking at it.
        unread: !isActive && eventTs > baseline,
      };
    }
    return out;
  }, [visibleSessions, liveForeground, seen, currentSessionId]);

  const grouped = React.useMemo(() => groupByRecency(visibleSessions), [visibleSessions]);
  const handleDeletedSession = React.useCallback(
    (sessionId: string) => {
      // Drop the row immediately and keep it gone: remove it from the loaded
      // list and remember the id so the poll / live-foreground synth can't
      // bring it back before (or instead of) the server-side disappearance.
      setSessions((prev) => (prev ? prev.filter((s) => s.session_id !== sessionId) : prev));
      setDeletedIds((prev) => {
        if (prev.has(sessionId)) return prev;
        const next = new Set(prev);
        next.add(sessionId);
        return next;
      });
      clearSessionPrefs(workspaceId, cwd, sessionId);
      if (sessionId === currentSessionId) {
        onChooseSession(nextSessionIdAfterDelete(visibleSessions, sessionId));
      }
    },
    [workspaceId, cwd, currentSessionId, onChooseSession, visibleSessions]
  );

  return (
    <aside className={cn('flex h-full min-h-0 select-none flex-col bg-white dark:bg-[#0d1013]', className)}>
      <SidebarHeader cwd={cwd} />

      <div className="agent-scroll flex min-h-0 flex-1 flex-col overflow-y-auto pb-3">
        {error && (
          <ErrorState icon={MessagesSquare} title="Couldn't load sessions" description={error} onRetry={load} />
        )}

        {!error && sessions == null && visibleSessions.length === 0 && (
          <div className="flex items-center justify-center py-6 text-gray-300 dark:text-gray-600">
            <Spinner size="size-3.5" />
          </div>
        )}

        {!error && sessions && <NewSessionButton onClick={() => onChooseSession(null)} />}

        {!error && sessions && visibleSessions.length === 0 && (
          <div className="my-auto flex flex-col items-center px-7 text-center">
            <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-gray-50 text-gray-300 ring-1 ring-gray-100 dark:bg-white/[0.04] dark:text-gray-600 dark:ring-white/[0.06]">
              <MessagesSquare className="size-4" strokeWidth={1.75} />
            </div>
            <p className="font-medium text-[12.5px] text-gray-500 dark:text-gray-400">No sessions yet</p>
            <p className="mt-1 max-w-[180px] text-[11.5px] text-gray-400 leading-relaxed dark:text-gray-500">
              Start a chat and it will appear here.
            </p>
          </div>
        )}

        {!error && visibleSessions.length > 0 && (
          <div className="flex flex-col gap-1">
            {grouped.map((group, index) => (
              <SessionGroup
                key={group.label}
                label={group.label}
                first={index === 0}
                cwd={cwd}
                sessions={group.sessions}
                currentSessionId={currentSessionId}
                onChooseSession={(id) => {
                  // Mark the row seen at click time so the dot
                  // extinguishes immediately, before the new
                  // session's runtime even attaches.
                  if (id) {
                    const status = rowStatuses[id];
                    const live = liveForeground?.sessionId === id ? liveForeground.lastEventAt : 0;
                    const session = group.sessions.find((s) => s.session_id === id);
                    const disk = session ? diskActivityAt(session) : 0;
                    const ts = Math.max(live, disk);
                    if (ts && status) markSeen(id, ts);
                  }
                  onChooseSession(id);
                }}
                onChanged={refreshLoaded}
                onDeletedSession={handleDeletedSession}
                statuses={rowStatuses}
              />
            ))}
            {hasMoreSessions && <LoadMoreSessionsButton loading={loadingMoreSessions} onClick={loadMoreSessions} />}
          </div>
        )}
      </div>
    </aside>
  );
}

function LoadMoreSessionsButton({ loading, onClick }: { loading: boolean; onClick: () => void }) {
  return (
    <div className="px-3 pt-2">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={cn(
          'flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 rounded-md px-3',
          'font-medium text-[12px] text-gray-500 transition-colors',
          'hover:bg-gray-50 hover:text-gray-800 disabled:cursor-default disabled:opacity-60',
          'dark:text-gray-400 dark:hover:bg-white/[0.04] dark:hover:text-gray-200'
        )}
      >
        {loading && <Spinner size="size-3.5" />}
        Older sessions
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Header
// -----------------------------------------------------------------------------

interface SidebarHeaderProps {
  cwd: string;
}

export function mergeLiveForegroundSession(
  sessions: AgentSessionEntry[],
  liveForeground: LiveForegroundSession | null
): AgentSessionEntry[] {
  const byId = new Map<string, AgentSessionEntry>();
  for (const session of sessions) {
    byId.set(session.session_id, session);
  }
  if (liveForeground && byId.has(liveForeground.sessionId)) {
    const existing = byId.get(liveForeground.sessionId);
    if (existing) {
      byId.set(liveForeground.sessionId, {
        ...existing,
        // Opening an old session can replay persisted frames and
        // advance the foreground reducer's live timestamp to "now".
        // Recency must stay tied to server-derived session metadata;
        // only in-progress state is safe to overlay locally.
        is_active: liveForeground.inProgress,
      });
    }
  } else if (liveForeground) {
    const ts = liveForeground.lastEventAt || Date.now();
    byId.set(liveForeground.sessionId, {
      session_id: liveForeground.sessionId,
      first_prompt: null,
      summary: null,
      last_modified: ts,
      last_event_at: ts,
      created_at: ts,
      is_active: liveForeground.inProgress,
    });
  }
  return [...byId.values()];
}

function SidebarHeader({ cwd }: SidebarHeaderProps) {
  const projectName = basename(cwd);
  const collapsedPath = collapseHome(cwd);
  return (
    <header className="flex flex-none items-center px-3 pt-3 pb-3">
      <div className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-white/[0.05] dark:text-gray-400">
          <Box className="size-[18px]" strokeWidth={1.8} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
          <div className="min-w-0 max-w-full truncate font-semibold text-[14px] text-gray-900 dark:text-gray-100">
            {projectName}
          </div>
          <div className="min-w-0 max-w-full truncate font-mono text-[10.5px] text-gray-400 leading-tight dark:text-gray-500">
            {collapsedPath}
          </div>
        </div>
      </div>
    </header>
  );
}

function NewSessionButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="px-3 pb-2">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-dashed px-3',
          'font-medium text-[12.5px] text-gray-500 transition-[border-color,background-color,color]',
          'border-gray-200 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
          'dark:border-white/10 dark:text-gray-400 dark:hover:border-white/15 dark:hover:bg-white/[0.06] dark:hover:text-gray-200'
        )}
      >
        <Plus className="size-3.5" strokeWidth={1.8} />
        New session
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Recency grouping
// -----------------------------------------------------------------------------

interface SessionGroupData {
  label: string;
  sessions: AgentSessionEntry[];
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Bucket the sidebar's session list into recency groups. Mirrors
 * the affordance the inspirational screenshot calls out — Today /
 * Earlier — but split a touch finer ("Yesterday", "This week",
 * "Earlier") so a 200-session project doesn't read as one giant
 * undifferentiated wall under "Earlier".
 *
 * Recency uses :func:`diskActivityAt` so sessions only bubble back
 * to the top when a real user-visible turn lands — metadata writes
 * and synthetic auth-failure frames don't pollute the sort.
 */
function groupByRecency(sessions: AgentSessionEntry[]): SessionGroupData[] {
  if (sessions.length === 0) return [];
  const sorted = [...sessions].sort((a, b) => diskActivityAt(b) - diskActivityAt(a));

  const now = Date.now();
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const todayCut = startOfDay.getTime();
  const yesterdayCut = todayCut - DAY_MS;
  const weekCut = todayCut - 7 * DAY_MS;

  const buckets: SessionGroupData[] = [
    { label: 'Today', sessions: [] },
    { label: 'Yesterday', sessions: [] },
    { label: 'This week', sessions: [] },
    { label: 'Earlier', sessions: [] },
  ];

  for (const session of sorted) {
    const ts = diskActivityAt(session);
    if (ts >= todayCut) buckets[0].sessions.push(session);
    else if (ts >= yesterdayCut) buckets[1].sessions.push(session);
    else if (ts >= weekCut) buckets[2].sessions.push(session);
    else buckets[3].sessions.push(session);
  }

  return buckets.filter((b) => b.sessions.length > 0);
}

interface SessionGroupProps {
  label: string;
  first: boolean;
  cwd: string;
  sessions: AgentSessionEntry[];
  currentSessionId: string | null;
  onChooseSession: (sessionId: string | null) => void;
  onChanged: () => void;
  onDeletedSession: (sessionId: string) => void;
  /** Per-session fused live + disk status. */
  statuses: Record<string, RowStatus>;
}

function SessionGroup({
  label,
  first,
  cwd,
  sessions,
  currentSessionId,
  onChooseSession,
  onChanged,
  onDeletedSession,
  statuses,
}: SessionGroupProps) {
  return (
    <section className="flex flex-col">
      <div
        className={cn(
          'px-3 pb-1 font-semibold text-[11px] text-gray-400 uppercase tracking-[0.12em] dark:text-gray-500',
          first ? 'pt-1' : 'pt-2.5'
        )}
      >
        {label}
      </div>
      <ul className="flex flex-col px-3">
        {sessions.map((s) => {
          const status = statuses[s.session_id] ?? { inProgress: false, unread: false };
          return (
            <SessionRow
              key={s.session_id}
              cwd={cwd}
              session={s}
              active={s.session_id === currentSessionId}
              inProgress={status.inProgress}
              unread={status.unread}
              onResume={() => onChooseSession(s.session_id)}
              onChanged={onChanged}
              onDeleted={() => onDeletedSession(s.session_id)}
            />
          );
        })}
      </ul>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Session row
// -----------------------------------------------------------------------------

interface SessionRowProps {
  cwd: string;
  session: AgentSessionEntry;
  active: boolean;
  /** ``true`` when the agent is actively producing output for this session. */
  inProgress: boolean;
  /** ``true`` when the session has new activity since the user last viewed it. */
  unread: boolean;
  onResume: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}

function SessionRow({ cwd, session, active, inProgress, unread, onResume, onChanged, onDeleted }: SessionRowProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const generatedLabel = generatedSessionLabel(session);
  const hasGeneratedLabel = generatedLabel !== null;
  const label = generatedLabel ?? 'Generating session name...';
  // Recency caption follows the same rule the row sorts by: trust
  // a numeric ``last_event_at``, fall through to ``created_at`` for
  // metadata-only sessions where the server scanned and found no
  // user-visible turn, and only fall back to ``last_modified`` when
  // the server didn't compute the field at all (older builds).
  const updatedLabel = formatRelativeFromEpochMs(displayActivityAt(session));

  async function submitRename(e: React.FormEvent) {
    e.preventDefault();
    const title = draft.trim();
    if (!title) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await renameSession(cwd, session.session_id, title);
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function runDelete() {
    setBusy(true);
    setDeleteError(null);
    try {
      // The server is idempotent: a missing JSONL surfaces as
      // ``already_deleted: true`` instead of 500-ing. Either way the
      // row should go away and the listing should refresh.
      await deleteSession(cwd, session.session_id);
      setDeleteOpen(false);
      onDeleted();
      onChanged();
    } catch (err) {
      // Keep the modal open with an inline error so the user can see
      // why nothing happened (was a swallowed promise rejection, which
      // read as "click does nothing"). The disk state may still have
      // changed (close_session ran before the error inside the delete
      // handler), so refresh the listing too.
      const message = err instanceof Error ? err.message : String(err);
      setDeleteError(`Couldn't delete this session: ${message}. Refreshing the list — try again.`);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  function cancelEditing() {
    setEditing(false);
    setDraft('');
  }

  return (
    <li
      className={cn(
        // Fixed row height so the list never jumps between the
        // single-line static layout and the single-line editing
        // form. Both center vertically inside the same box.
        'group relative h-[34px] rounded-md transition-colors',
        editing
          ? 'bg-gray-100/80 dark:bg-white/[0.06]'
          : active
            ? 'bg-gray-100 dark:bg-white/[0.06]'
            : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'
      )}
    >
      {editing ? (
        <form onSubmit={submitRename} className="flex h-full items-center gap-1.5 px-2">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEditing();
              }
            }}
            disabled={busy}
            placeholder="Session name"
            className="min-w-0 flex-1 select-text border-none bg-transparent text-[13.5px] text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
          {busy ? (
            <Spinner size="size-3.5" className="text-gray-500 dark:text-gray-400" />
          ) : (
            <>
              <button
                type="submit"
                disabled={!draft.trim()}
                className={cn(
                  'shrink-0 cursor-pointer rounded px-1.5 py-0.5 font-medium text-[11px] text-gray-700 transition-colors',
                  'hover:bg-gray-200/70 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent',
                  'dark:text-gray-300 dark:hover:bg-white/10 dark:disabled:hover:bg-transparent'
                )}
              >
                Save
              </button>
              <button
                type="button"
                aria-label="Cancel"
                onClick={cancelEditing}
                className={cn(
                  'shrink-0 cursor-pointer rounded p-0.5 text-gray-400 transition-colors',
                  'hover:bg-gray-100 hover:text-gray-700',
                  'dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-200'
                )}
              >
                <X className="size-3.5" />
              </button>
            </>
          )}
        </form>
      ) : (
        <>
          {unread && !inProgress && (
            <span className="pointer-events-none absolute inset-y-0 left-1.5 flex items-center justify-center">
              <span
                role="img"
                aria-label="Unread activity"
                title="New activity since you last opened this session"
                className="size-1.5 rounded-full bg-blue-500 dark:bg-blue-400"
              />
            </span>
          )}
          <button
            type="button"
            onClick={onResume}
            className={cn(
              'flex h-full w-full min-w-0 cursor-pointer items-center gap-2 pr-2 text-left',
              unread && !inProgress ? 'pl-5' : 'pl-2'
            )}
          >
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-[13.5px] leading-5',
                !hasGeneratedLabel && 'font-normal text-gray-400 italic dark:text-gray-500',
                active
                  ? hasGeneratedLabel && 'font-medium text-gray-900 dark:text-gray-100'
                  : unread
                    ? hasGeneratedLabel && 'text-gray-800 dark:text-gray-200'
                    : hasGeneratedLabel && 'text-gray-700 dark:text-gray-300'
              )}
            >
              {label}
            </span>
            {inProgress ? (
              <Spinner
                aria-hidden={false}
                aria-label="Session is running"
                size="size-3.5"
                className="text-gray-500 transition-opacity group-hover:opacity-0 dark:text-gray-400"
              />
            ) : updatedLabel ? (
              <span
                className={cn(
                  'shrink-0 text-[11px] text-gray-400 leading-5 transition-opacity dark:text-gray-500',
                  // Hide on hover so the row actions can occupy the
                  // same trailing slot — keeps the row calm at rest
                  // and avoids layout shift.
                  'group-hover:opacity-0'
                )}
              >
                {updatedLabel}
              </span>
            ) : null}
          </button>
          {/* Row actions — overlay the date area on hover so
              the row never grows. The active row stays calm at
              rest too: selection alone doesn't accumulate visual
              weight. Keyboard tabbing can still focus the buttons
              directly because they remain in the tab order. */}
          <div
            className={cn(
              'pointer-events-none absolute inset-y-0 right-1.5 flex items-center gap-0.5 opacity-0 transition-opacity',
              'group-hover:pointer-events-auto group-hover:opacity-100'
            )}
          >
            <SimpleTooltip content="Rename" side="bottom">
              <button
                type="button"
                aria-label="Rename session"
                disabled={busy}
                onClick={() => {
                  setDraft(session.summary ?? '');
                  setEditing(true);
                }}
                className={cn(
                  'flex size-6 cursor-pointer items-center justify-center rounded text-gray-400 transition-colors',
                  'hover:bg-gray-200/70 hover:text-gray-700',
                  'dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-200'
                )}
              >
                <Pencil className="size-3.5" strokeWidth={1.75} />
              </button>
            </SimpleTooltip>
            <SimpleTooltip content="Delete" side="bottom">
              <button
                type="button"
                aria-label="Delete session"
                disabled={busy}
                onClick={() => setDeleteOpen(true)}
                className={cn(
                  'flex size-6 cursor-pointer items-center justify-center rounded text-gray-400 transition-colors',
                  'hover:bg-red-50 hover:text-red-600',
                  'dark:text-gray-500 dark:hover:bg-red-500/10 dark:hover:text-red-400'
                )}
              >
                <Trash2 className="size-3.5" strokeWidth={1.75} />
              </button>
            </SimpleTooltip>
          </div>
        </>
      )}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (!open) setDeleteError(null);
        }}
        title="Delete session"
        description={
          <>
            <span className="block">
              This will permanently delete{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">"{label}"</span>.
            </span>
            <span className="mt-1 block text-gray-500 dark:text-gray-500">
              The conversation history can't be recovered.
            </span>
          </>
        }
        confirmLabel="Delete session"
        onConfirm={runDelete}
        loading={busy}
        errorMessage={deleteError}
      />
    </li>
  );
}

function basename(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx >= 0 ? path.slice(idx + 1) : path;
}

function generatedSessionLabel(session: AgentSessionEntry): string | null {
  const summary = session.summary?.trim();
  return summary ? summary : null;
}

/** Collapse a path against ``$HOME`` so long absolute paths read as ``~/...``. */
function collapseHome(path: string): string {
  // The browser doesn't know the user's home dir, so we fall back to
  // a best-effort match against any ``/home/<user>`` or
  // ``/Users/<user>`` prefix.
  const m = path.match(/^(\/home\/[^/]+|\/Users\/[^/]+)/);
  if (!m) return path;
  return `~${path.slice(m[1].length)}`;
}
